import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { Skeleton } from "@/components/ui/skeleton";
import { CommentComposer } from "./comment-composer";
import { TimelineItem } from "./timeline-item";
import { type Entry, type Filter, FILTERS } from "./timeline-types";

export type { Entry, Filter };

interface ActivityTimelineProps {
  canonicalId: string;
  projectId: string;
  canComment: boolean;
}

/**
 * One chronological list per finding: comments, assignment, state
 * changes, tracker links and states, and runs that saw (or no longer
 * saw) it. Run events are derived from the run ledger, not stored.
 */
export function ActivityTimeline({
  canonicalId,
  projectId,
  canComment,
}: ActivityTimelineProps) {
  const [filter, setFilter] = useState<Filter>("all");
  const { auth } = useAuth();
  const utils = trpc.useUtils();
  const timeline = trpc.workflow.timeline.useInfiniteQuery(
    { canonicalId, filter, limit: 30 },
    {
      getNextPageParam: (last) => last.nextBefore ?? undefined,
      initialCursor: null,
    },
  );
  const { data: members } = trpc.project.members.useQuery({ projectId });
  const names = useMemo(
    () => new Map((members ?? []).map((m) => [m.userId, m.displayName])),
    [members],
  );
  const entries: Entry[] = timeline.data?.pages.flatMap((p) => p.entries) ?? [];

  const refresh = () => {
    utils.workflow.timeline.invalidate({ canonicalId });
    utils.workflow.projectActivity.invalidate({ projectId });
  };

  return (
    <section aria-labelledby="activity-heading" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Kicker as="h2">
          <span id="activity-heading">Activity</span>
        </Kicker>
        <div role="group" aria-label="Show activity" className="flex gap-1">
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              size="sm"
              variant={filter === f.value ? "outline" : "ghost"}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {canComment && (filter === "all" || filter === "comments") && (
        <CommentComposer
          canonicalId={canonicalId}
          members={members ?? []}
          onPosted={refresh}
        />
      )}

      {timeline.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {filter === "comments"
            ? "No notes yet."
            : "Nothing has happened to this finding yet."}
        </p>
      ) : (
        <ol className="relative ml-2 space-y-4 border-l border-border pt-1">
          {entries.map((e) => (
            <TimelineItem
              key={e.id}
              entry={e}
              projectId={projectId}
              names={names}
              isOwn={!!auth?.userId && e.actorId === auth.userId}
              onChanged={refresh}
            />
          ))}
        </ol>
      )}
      {timeline.hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => timeline.fetchNextPage()}
          disabled={timeline.isFetchingNextPage}
        >
          {timeline.isFetchingNextPage ? "Loading…" : "Show older activity"}
        </Button>
      )}
    </section>
  );
}
